import { useEffect, useState } from 'react';

import flagsSpriteUrl from '@/assets/flags.svg';

export type CountryFlagSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_IN_PX: Record<CountryFlagSize, number> = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 40,
};

/** Only ISO 3166-1 alpha-2 codes are valid sprite symbol ids. */
function normalizeCountryCode(countryCode: string) {
  const code = countryCode.toUpperCase();

  return /^[A-Z]{2}$/.test(code) ? code : undefined;
}

const flagUrlCache = new Map<string, string | undefined>();
let spritePromise: Promise<Document | null> | null = null;

function loadSprite() {
  spritePromise ??= fetch(flagsSpriteUrl)
    .then((response) => (response.ok ? response.text() : null))
    .then((sprite) =>
      sprite ? new DOMParser().parseFromString(sprite, 'image/svg+xml') : null,
    )
    .catch(() => null);

  return spritePromise;
}

/**
 * Extracts a single flag from the sprite as a standalone `data:` URL.
 * Needed by components that only accept an image URL (e.g. `CardBtn`'s `img`),
 * since `<img>` cannot render a `<symbol>` fragment reference.
 */
export async function getCountryFlagUrl(countryCode: string) {
  const code = normalizeCountryCode(countryCode);

  if (!code) {
    return undefined;
  }

  if (flagUrlCache.has(code)) {
    return flagUrlCache.get(code);
  }

  const symbol = (await loadSprite())?.getElementById(`flag-${code}`);
  const viewBox = symbol?.getAttribute('viewBox');

  const url =
    symbol && viewBox
      ? `data:image/svg+xml,${encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}">${symbol.innerHTML}</svg>`,
        )}`
      : undefined;

  flagUrlCache.set(code, url);

  return url;
}

/** Resolves a flag `data:` URL for the given country, `undefined` while loading. */
export function useCountryFlagUrl(countryCode?: string | null) {
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!countryCode) {
      setUrl(undefined);

      return;
    }

    let isActive = true;

    getCountryFlagUrl(countryCode).then((flagUrl) => {
      if (isActive) {
        setUrl(flagUrl);
      }
    });

    return () => {
      isActive = false;
    };
  }, [countryCode]);

  return url;
}

export interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 country code, e.g. `FR`. */
  countryCode: string;
  /** Rendered size. Defaults to `sm` (24px). */
  size?: CountryFlagSize;
  /** Clip the flag inside a circle. */
  filled?: boolean;
  className?: string;
}

/** Renders a country flag by referencing the shared `flags.svg` sprite. */
export function CountryFlag({
  countryCode,
  size = 'sm',
  filled = false,
  className,
}: Readonly<CountryFlagProps>) {
  const code = normalizeCountryCode(countryCode);

  if (!code) {
    return null;
  }

  const sizeInPx = SIZE_IN_PX[size];

  return (
    <svg
      className={className}
      width={sizeInPx}
      height={sizeInPx}
      aria-hidden="true"
      style={{
        verticalAlign: 'middle',
        borderRadius: filled ? '50%' : 2,
      }}>
      <use href={`${flagsSpriteUrl}#flag-${code}`} />
    </svg>
  );
}

export default CountryFlag;
